// Client-side encryption utilities
// This ensures that even the backend/database admin cannot read user data

class EncryptionService {
  constructor() {
    this.keyName = 'itsmyvibe_encryption_key';
    this.algorithm = 'AES-GCM';
    this.keyLength = 256;
  }

  // Generate or retrieve encryption key
  async getOrCreateKey() {
    // Try to get existing key from localStorage
    const storedKey = localStorage.getItem(this.keyName);
    
    if (storedKey) {
      // Import existing key
      const keyData = JSON.parse(storedKey);
      return await crypto.subtle.importKey(
        'jwk',
        keyData,
        { name: this.algorithm },
        true,
        ['encrypt', 'decrypt']
      );
    }

    // Generate new key
    const key = await crypto.subtle.generateKey(
      {
        name: this.algorithm,
        length: this.keyLength,
      },
      true,
      ['encrypt', 'decrypt']
    );

    // Store key for future use
    const exportedKey = await crypto.subtle.exportKey('jwk', key);
    localStorage.setItem(this.keyName, JSON.stringify(exportedKey));

    return key;
  }

  // Encrypt data
  async encrypt(plaintext) {
    try {
      const key = await this.getOrCreateKey();
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(plaintext));
      
      // Generate random IV
      const iv = crypto.getRandomValues(new Uint8Array(12));
      
      // Encrypt
      const encrypted = await crypto.subtle.encrypt(
        {
          name: this.algorithm,
          iv: iv,
        },
        key,
        data
      );

      // Combine IV and encrypted data
      const result = {
        iv: Array.from(iv),
        data: Array.from(new Uint8Array(encrypted)),
      };

      return btoa(JSON.stringify(result));
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  // Decrypt data
  async decrypt(encryptedData) {
    try {
      const key = await this.getOrCreateKey();
      const { iv, data } = JSON.parse(atob(encryptedData));
      
      // Convert arrays back to Uint8Arrays
      const ivArray = new Uint8Array(iv);
      const dataArray = new Uint8Array(data);
      
      // Decrypt
      const decrypted = await crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv: ivArray,
        },
        key,
        dataArray
      );

      // Convert back to string and parse JSON
      const decoder = new TextDecoder();
      const decryptedText = decoder.decode(decrypted);
      return JSON.parse(decryptedText);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  // Clear encryption key (for logout/reset)
  clearKey() {
    localStorage.removeItem(this.keyName);
  }

  // Check if encryption is supported
  isSupported() {
    return 'crypto' in window && 'subtle' in window.crypto;
  }
}

export const encryptionService = new EncryptionService();